import {
  forwardRef,
  useImperativeHandle,
  useMemo,
  useState,
  useEffect,
  useRef,
} from "react";
import { Box, Space, Text, Title, Modal, Button, Group } from "@mantine/core";
import { Controller, useForm } from "react-hook-form";
import { ButtonsSelect } from "../../button/ButtonsSelect";
import { useMutation } from "@apollo/client";
import { Product } from "../../../types/interfaces";
import { ItemType, RemovableItemOrderType } from "../../../types/enums";
import { UPDATE_ORDER } from "../../../gql/orders";
import {
  formatChoicesData,
  getTeethsFromLowerArch,
  getTeethsFromUpperArch,
} from "../../../utils/item.utils";
import { UPDATE_REMOVABLE_ITEM } from "../../../gql/items";

import type { GetRemovableItemChoicesQuery } from "../../../gql/generated/graphql";

type PartialDentureMaterialFormData = {
  itemMaterialId: string;
  customTray: boolean;
  customTrayArch: "MAXILLAIRE" | "MANDIBULAIRE" | "BOTH" | null;
  isBimaxillarySeparated: boolean;
  maxillaireMaterialId?: string;
  mandibulaireMaterialId?: string;
};

type CreateRemovableItemPartialDentureMaterialProps = {
  product: Product;
  removableItemChoicesData: GetRemovableItemChoicesQuery;
  isConfirmationMode?: boolean;
  previousProduct?: Product;
};

export interface PartialDentureMaterialRef {
  saveData: () => Promise<void>;
}

export const CreateRemovableItemPartialDentureMaterial = forwardRef<
  PartialDentureMaterialRef,
  CreateRemovableItemPartialDentureMaterialProps
>(({ product, removableItemChoicesData }, ref) => {
  const removableItems = product.removableItem ?? [];

  const firstItem = removableItems[0];

  const isBothJaws = useMemo(() => {
    const allTeeth = removableItems.flatMap(item => item.teeth ?? []);
    const hasMaxillaire = allTeeth.some(tooth => tooth <= 28);
    const hasMandibulaire = allTeeth.some(tooth => tooth >= 31);

    return hasMaxillaire && hasMandibulaire;
  }, [removableItems]);

  const isWaxBite =
    firstItem?.partialDentureOrderType === RemovableItemOrderType.WAX_BITE;

  const materialChoices = useMemo(
    () =>
      formatChoicesData(removableItemChoicesData?.getItemMaterialsWhere ?? []),
    [removableItemChoicesData?.getItemMaterialsWhere]
  );

  const existingCustomTray = useMemo(() => {
    return removableItems.some(item => item?.partialDenture?.customTray);
  }, [removableItems]);

  const maxillaireItems = removableItems.filter(item =>
    (item.teeth ?? []).some(t => t <= 28)
  );
  const mandibulaireItems = removableItems.filter(item =>
    (item.teeth ?? []).some(t => t >= 31)
  );

  const [cancelToggleModal, setcancelToggleModal] = useState(false);
  const previousSingleMaterialRef = useRef(firstItem?.itemMaterial?.id);

  const {
    control,
    watch,
    setValue,
    formState: { errors },
    trigger,
  } = useForm<PartialDentureMaterialFormData>({
    defaultValues: {
      itemMaterialId: firstItem?.itemMaterial?.id,
      customTray: existingCustomTray,
      customTrayArch: isBothJaws ? "BOTH" : null,
      isBimaxillarySeparated: firstItem?.isBimaxillarySeparated ?? false,
      maxillaireMaterialId:
        maxillaireItems[0]?.itemMaterial?.id ?? firstItem?.itemMaterial?.id,
      mandibulaireMaterialId:
        mandibulaireItems[0]?.itemMaterial?.id ?? firstItem?.itemMaterial?.id,
    },
  });

  const [updateRemovableItem] = useMutation(UPDATE_REMOVABLE_ITEM);

  const [updateOrder] = useMutation(UPDATE_ORDER);

  const itemMaterialId = watch("itemMaterialId");
  const customTray = watch("customTray");
  const customTrayArch = watch("customTrayArch");

  const isBimaxillarySeparated = watch("isBimaxillarySeparated");
  const maxillaireMaterialId = watch("maxillaireMaterialId");
  const mandibulaireMaterialId = watch("mandibulaireMaterialId");

  const previousIsSeparatedRef = useRef(
    firstItem?.isBimaxillarySeparated ?? false
  );

  useEffect(() => {
    const wasSeparated = previousIsSeparatedRef.current;
    const isNowSeparated = isBimaxillarySeparated;

    if (wasSeparated && !isNowSeparated) {
      setcancelToggleModal(true);
    } else if (!wasSeparated && isNowSeparated) {
      previousSingleMaterialRef.current = itemMaterialId;
      setValue("maxillaireMaterialId", itemMaterialId);
      setValue("mandibulaireMaterialId", itemMaterialId);
    }

    previousIsSeparatedRef.current = isNowSeparated;
  }, [isBimaxillarySeparated]);

  const handleConfirmToggleOff = () => {
    setValue("itemMaterialId", previousSingleMaterialRef.current);
    setcancelToggleModal(false);
  };

  const handleCancelToggleOff = () => {
    setValue("isBimaxillarySeparated", true);
    setcancelToggleModal(false);
  };

  const customTrayArchOptions = [
    { value: "MAXILLAIRE", label: "Uniquement maxillaire" },
    { value: "MANDIBULAIRE", label: "Uniquement mandibulaire" },
    { value: "BOTH", label: "Les deux" },
  ];

  const saveData = async (): Promise<void> => {
    if (!removableItems.length) return;

    const isValidForm = await trigger();
    if (!isValidForm) {
      throw new Error("validation_error");
    }

    if (isBimaxillarySeparated) {
      await Promise.all(
        removableItems.map(item => {
          const teeth = item.teeth ?? [];
          const isMaxillaire = teeth.some(t => t <= 28);
          const materialId = isMaxillaire
            ? maxillaireMaterialId
            : mandibulaireMaterialId;
          return updateRemovableItem({
            variables: {
              where: { id: item.id },
              data: {
                itemMaterial: materialId
                  ? { connect: { id: materialId } }
                  : undefined,
                isBimaxillarySeparated: { set: true },
              },
            },
          });
        })
      );
    } else {
      if (!itemMaterialId) return;
      await Promise.all(
        removableItems.map(item =>
          updateRemovableItem({
            variables: {
              where: { id: item.id },
              data: {
                itemMaterial: { connect: { id: itemMaterialId } },
                isBimaxillarySeparated: { set: false },
              },
            },
          })
        )
      );
    }

    if (customTray && !existingCustomTray) {
      const allTeeth = removableItems.flatMap(item => item.teeth ?? []);
      const lowerTeeth = getTeethsFromLowerArch(allTeeth);
      const upperTeeth = getTeethsFromUpperArch(allTeeth);

      let removableItemsToCreate: { itemType: string; teeth: number[] }[];

      if (customTrayArch === "MAXILLAIRE" && upperTeeth.length) {
        removableItemsToCreate = [
          { itemType: ItemType.CUSTOM_TRAY, teeth: upperTeeth },
        ];
      } else if (customTrayArch === "MANDIBULAIRE" && lowerTeeth.length) {
        removableItemsToCreate = [
          { itemType: ItemType.CUSTOM_TRAY, teeth: lowerTeeth },
        ];
      } else {
        removableItemsToCreate = [
          ...(upperTeeth.length
            ? [{ itemType: ItemType.CUSTOM_TRAY, teeth: upperTeeth }]
            : []),
          ...(lowerTeeth.length
            ? [{ itemType: ItemType.CUSTOM_TRAY, teeth: lowerTeeth }]
            : []),
        ];
      }

      if (removableItemsToCreate.length > 0) {
        await updateOrder({
          variables: {
            where: { id: product.orderId },
            data: {
              products: {
                create: {
                  productType: ItemType.CUSTOM_TRAY,
                  teeth: allTeeth,
                  removableItem: {
                    create: removableItemsToCreate,
                  },
                },
              },
            },
          },
        });
      }
    }
  };

  useImperativeHandle(ref, () => ({ saveData }));

  if (!removableItems.length) {
    return (
      <div>
        <Text>
          {"Erreur sur l'ajout de produit. Veuillez contacter le support."}
        </Text>
      </div>
    );
  }

  return (
    <Box>
      {isBothJaws && (
        <>
          <Title order={4}>Séparer maxillaire et mandibule</Title>
          <Text size="sm" c="dimmed">
            Permet de choisir des matériaux différents pour chaque arcade
          </Text>
          <Space h="md" />
          <Controller
            name="isBimaxillarySeparated"
            control={control}
            render={({ field }) => (
              <ButtonsSelect
                data={[
                  { value: false, label: "Non" },
                  { value: true, label: "Oui" },
                ]}
                value={field.value}
                onChange={value => field.onChange(value)}
                numberOfColumns={2}
              />
            )}
          />
          <Space h="xl" />
        </>
      )}

      <Title order={3}>Matériau</Title>
      <Space h="md" />

      {!isBimaxillarySeparated ? (
        <>
          <Controller
            name="itemMaterialId"
            rules={{ required: true }}
            control={control}
            render={({ field }) => (
              <ButtonsSelect
                data={materialChoices}
                value={field.value}
                onChange={value => field.onChange(value)}
                numberOfColumns={2}
              />
            )}
          />
          {errors?.itemMaterialId?.type === "required" && (
            <Text size="sm" style={{ color: "red" }}>
              Veuillez sélectionner un matériau
            </Text>
          )}
        </>
      ) : (
        <>
          <Title order={4}>Maxillaire</Title>
          <Space h="xs" />
          <Controller
            name="maxillaireMaterialId"
            rules={{ required: true }}
            control={control}
            render={({ field }) => (
              <ButtonsSelect
                data={materialChoices}
                value={field.value}
                onChange={value => field.onChange(value)}
                numberOfColumns={2}
              />
            )}
          />
          {errors?.maxillaireMaterialId?.type === "required" && (
            <Text size="sm" style={{ color: "red" }}>
              Veuillez sélectionner un matériau pour le maxillaire
            </Text>
          )}
          <Space h="lg" />
          <Title order={4}>Mandibulaire</Title>
          <Space h="xs" />
          <Controller
            name="mandibulaireMaterialId"
            rules={{ required: true }}
            control={control}
            render={({ field }) => (
              <ButtonsSelect
                data={materialChoices}
                value={field.value}
                onChange={value => field.onChange(value)}
                numberOfColumns={2}
              />
            )}
          />
          {errors?.mandibulaireMaterialId?.type === "required" && (
            <Text size="sm" style={{ color: "red" }}>
              Veuillez sélectionner un matériau pour la mandibule
            </Text>
          )}
        </>
      )}

      {isWaxBite && (
        <>
          <Space h="xl" />
          <Title order={4}>Options</Title>
          <Text size="sm" fw={500}>
            Souhaitez-vous ajouter un porte-empreinte individuel ?
          </Text>
          <Space h="xs" />
          <Controller
            name="customTray"
            control={control}
            render={({ field }) => (
              <ButtonsSelect
                data={[
                  { value: false, label: "Non" },
                  { value: true, label: "Oui" },
                ]}
                value={field.value}
                onChange={value => field.onChange(value)}
                numberOfColumns={2}
              />
            )}
          />

          {customTray && isBothJaws && (
            <>
              <Space h="md" />
              <Text size="sm" fw={500}>
                Pour quelle(s) arcade(s) ?
              </Text>
              <Space h="xs" />
              <Controller
                name="customTrayArch"
                control={control}
                render={({ field }) => (
                  <ButtonsSelect
                    data={customTrayArchOptions}
                    value={field.value}
                    onChange={value => field.onChange(value)}
                    numberOfColumns={3}
                  />
                )}
              />
            </>
          )}
          {customTray && (
            <>
              <Space h="sm" />
              <Text size="sm" c="blue">
                Un porte-empreinte individuel sera ajouté à votre commande
              </Text>
            </>
          )}
        </>
      )}
      <Space h="xl" />
      <Modal
        opened={cancelToggleModal}
        onClose={handleCancelToggleOff}
        title="Confirmation"
      >
        <Text>
          Vos sélections par arcade seront perdues. Voulez-vous continuer ?
        </Text>
        <Space h="md" />
        <Group justify="right">
          <Button variant="outline" onClick={handleCancelToggleOff}>
            Annuler
          </Button>
          <Button onClick={handleConfirmToggleOff}>Continuer</Button>
        </Group>
      </Modal>
    </Box>
  );
});

CreateRemovableItemPartialDentureMaterial.displayName =
  "CreateRemovableItemPartialDentureMaterial";
